import moment from 'moment-timezone';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  DateRangePicker,
  Input,
  ModalUI,
  Selector,
  SelectorOptionPropsInterface
} from 'sonata-react-components';

import { countriesISO2Options } from '@/helpers/countriesOptions/countriesOptions.helper';
import { getUserId } from '@/helpers/user/user.helpers';

import './newDeal.scss';

import { DigitalAdExchangeCatalog, DOOHAdExchangeCatalog } from 'sonata-static-storage/lib/samples/adExchanges.samples';
import * as currencies from 'sonata-static-storage/lib/samples/currencies.samples';
import {
  type DealInterface,
  DealTypeEnum,
  SourceTypeEnum
} from 'sonata-static-storage/lib/schemas/premium/deal.schema';

import { AssignDealSection, type AssignedUser } from '@/components/common/AssignDealSection/AssignDealSection';
import { type DealConfig, saveDeal, updateDeal } from './helpers';

import type { Moment } from 'moment-timezone';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

type Props = {
  deal?: Partial<DealInterface<string>>;
  isModalOpened: boolean;
  onClose: () => void;
  onSave: () => void;
  user: UserInterface;
};

type State = {
  adExchange: DealInterface['origin']['name'] | '';
  alias: DealInterface['alias'] | '';
  id: DealInterface<string>['origin']['id'] | '';
  type: DealInterface['type'] | null;
  seller: DealInterface['seller'] | '';
  startDate: DealInterface['startDate'] | null;
  endDate: DealInterface['endDate'] | null;
  bidFloor: DealInterface['bidFloor'] | '';
  currencyCode: DealInterface['currencyCode'] | '';
  country: DealInterface['country'] | '';
  newDealAssignedUsers: AssignedUser[];
  sourceType: DealInterface['sourceType'] | '';
  isSaving: boolean;
};

type FormState = Omit<State, 'isSaving' | 'newDealAssignedUsers'>;

const adExchangeCatalog = {
  ...DigitalAdExchangeCatalog,
  ...DOOHAdExchangeCatalog
};

export function NewDeal({ deal, isModalOpened, onClose, onSave, user }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const modalRef = useRef<React.ElementRef<typeof ModalUI>>(null);
  const requiredFields: (keyof FormState)[] = ['alias', 'seller', 'bidFloor', 'currencyCode', 'sourceType', 'type'];

  const [{ newDealAssignedUsers, isSaving, ...state }, setState] = useState<State>({
    adExchange: deal?.origin?.name ?? '',
    alias: deal?.alias ?? '',
    bidFloor: deal?.bidFloor ?? '',
    country: deal?.country ?? '',
    currencyCode: deal?.currencyCode ?? '',
    endDate: deal?.endDate ?? null,
    id: deal?.origin?.id ?? '',
    isSaving: false,
    newDealAssignedUsers: [{ id: user.id, name: user.name }],
    seller: deal?.seller ?? '',
    sourceType: deal?.sourceType ?? '',
    startDate: deal?.startDate ?? null,
    type: deal?.type ?? null
  });

  const setNewDealAssignedUsers = (assignedUsers: AssignedUser[]) => {
    setState(prev => ({
      ...prev,
      newDealAssignedUsers: assignedUsers
    }));
  };

  useEffect(() => {
    if (isModalOpened) {
      modalRef?.current?.openModal?.();
    } else {
      modalRef?.current?.closeModal?.();
    }
  }, [isModalOpened]);

  const BASE_ID = 'newDeal';

  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);
  const handleSave = async () => {
    setState(prev => ({ ...prev, isSaving: true }));

    deal
      ? await updateDeal({ dealConfig: state as DealConfig, formatMessage, id: deal._id! })
      : await saveDeal({
          dealConfig: state as DealConfig,
          formatMessage,
          newDealAssignedUsers,
          userId: getUserId(user)
        });
    setState(prev => ({ ...prev, isSaving: false }));
    onSave();
  };

  const handleClose = () => {
    onClose();
  };

  const handleDatesChange = (dates: { endDate: Moment | null; startDate: Moment | null }) => {
    setState(prev => ({
      ...prev,
      endDate: dates.endDate?.toDate() ?? null,
      startDate: dates.startDate?.toDate() ?? null
    }));
  };

  const isCreateButtonDisabled = requiredFields.some(field => !state[field]) || isSaving;

  const dealTypeOptions = Object.values(DealTypeEnum).map(type => ({ id: type, label: type }));

  return (
    <ModalUI
      closeModalCallback={() => handleClose()}
      leftButton={
        <ButtonUI
          className="newDeal_backButton"
          icon="fa-solid fa-arrow-left"
          onClick={() => handleClose()}
          text={formatMessage({
            defaultMessage: 'Back',
            id: 'common.back'
          })}
          type="secondary"
        />
      }
      ref={modalRef}
      rightButton={
        <ButtonUI
          className="newDeal_createButton"
          disabled={isCreateButtonDisabled}
          icon="fa-regular fa-floppy-disk"
          loading={isSaving}
          onClick={handleSave}
          text={
            deal
              ? formatMessage({
                  defaultMessage: 'Update',
                  id: 'common.update'
                })
              : formatMessage({
                  defaultMessage: 'Create',
                  id: 'common.create'
                })
          }
          type="primary"
        />
      }
      title={
        deal?.name
          ? formatMessage({
              defaultMessage: 'Deal creation',
              id: 'menu.newDeal.title'
            })
          : formatMessage({
              defaultMessage: 'Deal details',
              id: 'menu.newDeal.details'
            })
      }
      width="678px"
    >
      <div className="newDeal">
        <div className="newDeal__form">
          <div className="newDeal__formRow">
            <div className="newDeal__formField">
              <Input
                id={`${BASE_ID}_alias`}
                label={formatMessage({ defaultMessage: 'Name', id: 'menu.newDeal.name' })}
                name="alias"
                onChange={value => setState(prev => ({ ...prev, alias: value as string }))}
                placeholder={formatMessage({ defaultMessage: 'Type a name...', id: 'menu.newDeal.namePlaceholder' })}
                shape="squareUI"
                type="text"
                value={state.alias}
              />
            </div>
            <div className="newDeal__formField">
              <Input
                id={`${BASE_ID}_id`}
                label={formatMessage({ defaultMessage: 'ID', id: 'menu.newDeal.id' })}
                name="id"
                onChange={value => setState(prev => ({ ...prev, id: value as string }))}
                placeholder={formatMessage({ defaultMessage: 'Deal ID', id: 'menu.newDeal.idPlaceholder' })}
                shape="squareUI"
                type="text"
                value={state.id}
              />
            </div>
          </div>
          <div className="newDeal__formRow">
            <div className="newDeal__formField">
              <Selector
                id={`${BASE_ID}_adExchangeSelector`}
                label={formatMessage({ defaultMessage: 'Ad Exchange', id: 'menu.newDeal.adExchange.label' })}
                onChange={(option: SelectorOptionPropsInterface) =>
                  setState(prev => ({ ...prev, adExchange: option.id as any }))
                }
                options={Object.values(adExchangeCatalog)
                  .filter(exchange => exchange.dealCreation)
                  .map(exchange => ({
                    id: exchange.code,
                    label: exchange.name
                  }))}
                showSearch={false}
                showSelectedItems={false}
                type="simple"
                value={state.adExchange ? [{ id: state.adExchange, label: state.adExchange }] : []}
              />
            </div>
            <div className="newDeal__formField">
              <Selector
                id={`${BASE_ID}_dealTypeSelector`}
                label={formatMessage({ defaultMessage: 'Deal Type', id: 'menu.newDeal.dealType.label' })}
                onChange={(option: SelectorOptionPropsInterface) =>
                  setState(prev => ({ ...prev, type: option.id as any }))
                }
                options={dealTypeOptions}
                showSearch={false}
                showSelectedItems={false}
                type="simple"
                value={state.type ? dealTypeOptions.filter(pt => pt.id === state.type) : []}
              />
            </div>
          </div>
          <div className="newDeal__formRow">
            <div className="newDeal__formField">
              <Input
                id={`${BASE_ID}_seller`}
                label={formatMessage({ defaultMessage: 'Seller', id: 'menu.newDeal.seller' })}
                name="seller"
                onChange={value => setState(prev => ({ ...prev, seller: value as string }))}
                placeholder={formatMessage({ defaultMessage: 'Seller', id: 'menu.newDeal.sellerPlaceholder' })}
                shape="squareUI"
                type="text"
                value={state.seller}
              />
            </div>
            <div className="newDeal__formField">
              {/* @ts-ignore */}
              <DateRangePicker
                endDate={state.endDate ? moment.utc(state.endDate) : null}
                id={`${BASE_ID}_dateRange`}
                label={formatMessage({ defaultMessage: 'Dates', id: 'menu.newDeal.dates' })}
                onDatesChange={handleDatesChange}
                startDate={state.startDate ? moment.utc(state.startDate) : null}
              />
            </div>
          </div>
          <div className="newDeal__formRow">
            <div className="newDeal__formField">
              <Input
                id={`${BASE_ID}_bidFloor`}
                label={formatMessage({ defaultMessage: 'BidFloor', id: 'menu.newDeal.bidFloor' })}
                name="bidFloor"
                onChange={value => setState(prev => ({ ...prev, bidFloor: Number(value) }))}
                placeholder={formatMessage({ defaultMessage: 'BidFloor', id: 'menu.newDeal.bidFloorPlaceholder' })}
                shape="squareUI"
                type="number"
                value={state.bidFloor}
              />
            </div>
            <div className="newDeal__formField">
              <Selector
                id={`${BASE_ID}_currencySelector`}
                label={formatMessage({ defaultMessage: 'Currency', id: 'menu.newDeal.currency.label' })}
                onChange={(option: SelectorOptionPropsInterface) =>
                  setState(prev => ({ ...prev, currencyCode: option.id as any }))
                }
                options={Object.values(currencies).map(currency => ({
                  id: currency.baseCurrency,
                  label: currency.baseCurrency
                }))}
                showSearch={false}
                showSelectedItems={false}
                type="simple"
                value={state.currencyCode ? [{ id: state.currencyCode, label: state.currencyCode }] : []}
              />
            </div>
          </div>
          <div className="newDeal__formRow">
            <div className="newDeal__formField">
              <Selector
                id={`${BASE_ID}_countrySelector`}
                label={formatMessage({ defaultMessage: 'Country', id: 'menu.newDeal.country.label' })}
                onChange={(option: SelectorOptionPropsInterface) =>
                  setState(prev => ({ ...prev, country: option.id as any }))
                }
                options={countriesISO2Options}
                showSearch={true}
                showSelectedItems={false}
                type="simple"
                value={state.country ? [{ id: state.country, label: state.country }] : []}
              />
            </div>
            <div className="newDeal__formField">
              <Selector
                id={`${BASE_ID}_sourceTypeSelector`}
                label={formatMessage({ defaultMessage: 'Source Type', id: 'menu.newDeal.sourceType.label' })}
                onChange={(option: SelectorOptionPropsInterface) =>
                  setState(prev => ({ ...prev, sourceType: option.id as any }))
                }
                options={Object.keys(SourceTypeEnum)
                  .filter(key => isNaN(Number(key)))
                  .map(sourceType => ({
                    id: sourceType,
                    label: sourceType as string
                  }))}
                showSearch={false}
                showSelectedItems={false}
                type="simple"
                value={state.sourceType ? [{ id: state.sourceType, label: state.sourceType.toString() }] : []}
              />
            </div>
          </div>
          {deal ? (
            <AssignDealSection deal={deal} user={user} />
          ) : (
            <AssignDealSection
              assignedUsers={newDealAssignedUsers}
              onSetAssignedUsers={setNewDealAssignedUsers}
              user={user}
            />
          )}
        </div>
      </div>
    </ModalUI>
  );
}
